import type { Translation } from "@/i18n/locales/types";

export const policies: Translation["policies"] = {
  title: "Policies",
  new: "New policy",
  detail: "Policy",
  list: {
    description:
      "Who can use which certificates and where. Without a policy that allows it, no certificate is used.",
    search: "Search by name or person",
    certificateFilter: "Certificate",
    allCertificates: "All certificates",
    stateFilter: "Status",
    allStates: "All statuses",
    states: {
      enabled: "Active",
      disabled: "Disabled",
      expired: "Out of validity",
    },
    emptyTitle: "No policies yet",
    emptyDescription:
      "Create one to say who can use each certificate and on which sites.",
    columns: {
      name: "Name",
      who: "Who",
      certificates: "Certificates",
      rules: "Rules",
      validity: "Validity",
      state: "Status",
    },
    groupLabel: "group {{name}}",
    nobody: "Nobody yet",
    denyCount_one: "{{count}} deny",
    denyCount_other: "{{count}} deny",
  },
  editor: {
    notFound: "This policy does not exist or has been deleted.",
    saved: "Policy saved",
    deleted: "Policy deleted",
    untitled: "Untitled",
    history: "History",
    conflictTitle: "Someone else changed this policy",
    conflictDescription:
      "While you were editing, another admin saved changes. Reload to see the current version (you will lose what you have not saved).",
    reload: "Reload",
    serverRejected: "The server did not accept the policy",
    deleteTitle: "Delete “{{name}}”?",
    deleteDescription:
      "People who had access through this policy lose it at once. The change log is kept.",
    deleteConfirm: "Delete policy",
    unsavedTitle: "You have unsaved changes",
    unsavedDescription: "If you leave now, they will be lost.",
    leave: "Leave without saving",
    name: "Name",
    namePlaceholder: "For example, AEAT without income tax",
    description: "Description",
    descriptionPlaceholder: "What this policy is for (optional)",
    who: "Who",
    groupChip: "Group {{name}} ({{count}})",
    suspendedUser: "Suspended: cannot use anything while suspended.",
    searchWho: "Search people or groups",
    groups: "Groups",
    people: "People",
    noSubjects: "Nobody yet: add people or groups for the policy to apply.",
    withCredentials: "With which certificates",
    searchCredentials: "Search certificates",
    where: "Where",
    denyWins: "Deny rules always win, wherever they are.",
    noRules: "Without rules, the policy allows nothing. Add an allow rule.",
    rulesLabel: "Rules",
    editRule: "Edit rule {{number}}",
    moveUp: "Move rule {{number}} up",
    moveDown: "Move rule {{number}} down",
    ruleMenu: "More actions for rule {{number}}",
    duplicate: "Duplicate",
    cannotSave: "Before you can save:",
  },
  issues: {
    nameRequired: "Give the policy a name.",
    credentialsRequired: "Add at least one certificate.",
    noRules: "Add at least one rule.",
    ruleUsesRequired: "Rule {{number}}: choose at least one use.",
    ruleDomainsRequired: "Rule {{number}}: add at least one domain.",
    required: "A required field is missing.",
    invalidDomain: "Rule {{number}}: “{{domain}}” is not a valid domain.",
    unknownGroup:
      "Rule {{number}}: the group “{{domain}}” is not in the catalog.",
    outsideCatalog:
      "Rule {{number}}: “{{domain}}” is outside the site catalog.",
    invalidPath: "Rule {{number}}: path {{path}} is not a valid expression.",
    invalidMethod: "Rule {{number}}: there is an invalid HTTP method.",
    invalidApp:
      "Rule {{number}}: for document signing, each app must be an origin (https://…) or a domain.",
    invalidDate: "A validity date is not valid.",
    invalidValidity: "The end date must be after the start date.",
    invalidLimit: "Limits must be positive whole numbers.",
    invalidFormat: "There is an empty signature format.",
    invalidRule: "Rule {{number}}: not valid.",
    pathsUnused: "Rule {{number}}: paths only apply to browsing.",
    pathsProxyOnly:
      "Rule {{number}}: paths only apply while the Mac app is open.",
    appsInformational:
      "Rule {{number}}: on login and browsing, apps are informational only.",
    methodsUnused: "Rule {{number}}: methods only apply to browsing.",
  },
  domainProblems: {
    invalid: "“{{domain}}” is not a valid domain: write x.y, *.x.y or @group.",
    "unknown-group": "The group “{{domain}}” is not in the catalog.",
    "outside-catalog":
      "“{{domain}}” is not in the website catalog (version {{version}}) and cannot be saved: each Mac's proxy CA only covers the catalog. Ask a Punto.Firma administrator to add it; each Mac then gets it with “Update allowed websites”.",
  },
  outsideCatalogPlatform:
    "“{{domain}}” is not in the website catalog (version {{version}}). Add it to the catalog and publish it; then the policy can be saved.",
  addToCatalog: "Add to the website catalog",
  rulePanel: {
    title: "Rule {{number}}",
    description:
      "Changes show up in the tester right away. They are saved with the policy.",
    revert: "Undo changes",
    done: "Done",
    effect: "Effect",
    denyWins: "A deny rule beats any allow rule.",
    targets: "Domains and paths",
    targetsHint:
      "A whole domain (covers its subdomains; *.domain, only the subdomains; @group, every site in the group) or a path: site.example.gob.es/path, which covers everything under it.",
    allDomains: "All",
    addTarget: "Add domain or path",
    searchCatalog: "Search the catalog or type a domain or path",
    noCatalogMatch: "Nothing in the catalog matches.",
    catalogGroups: "Catalog groups",
    catalogDomains: "Catalog domains",
    addTyped: "Add “{{domain}}”",
    domainsRequired: "Add at least one domain or path, or check “All”.",
    wholeDomainNeedsRule:
      "This rule is for paths: for a whole domain, add another rule.",
    pathNeedsRule:
      "A rule's paths belong to a single domain and don't mix with whole domains: for this path, add another rule.",
    pathsProxyOnly:
      "Path rules only apply while the Mac app is open: with the app closed, the browser can open the path with the session it already has.",
    ruleDescription: "Description (optional)",
    ruleDescriptionPlaceholder: "For example, income tax",
  },
  options: {
    title: "Options",
    show: "Change",
    hide: "Hide",
    validity: "Validity",
    enabled: "Policy active",
    validFrom: "From",
    validUntil: "Until",
    errors: {
      order: "The end date must be after the start date.",
    },
    summary: {
      always: "always",
      between: "from {{from}} to {{until}}",
      from: "from {{from}}",
      until: "until {{until}}",
      disabled: "disabled",
    },
  },
  history: {
    title: "Change history",
    description:
      "Who changed this policy, when, and what it looked like before and after.",
    empty: "No changes recorded.",
    revision: "revision {{revision}}",
    system: "System",
    before: "Before",
    after: "After",
    actions: {
      created: "Created",
      updated: "Changed",
      deleted: "Deleted",
    },
  },
  tester: {
    title: "Test",
    description:
      "With the same evaluator as the app and the server, including unsaved changes.",
    person: "Person",
    choosePerson: "Choose a person",
    certificate: "Certificate",
    chooseCertificate: "Choose a certificate",
    use: "Use",
    method: "Method",
    url: "URL",
    uses: {
      navigation: "Open a page",
      document: "Sign a document (afirma://)",
    },
    sede: "Site (signing server)",
    origin: "Website origin",
    originHint:
      "The website asking for the signature. If given, it must also be in the rule's domains, not just the site.",
    fillIn: "Choose a person and a certificate and type a URL.",
    invalidUrl: "The URL is not valid.",
    normalizedPath: "Path compared:",
    checking: "Checking…",
    checkServer: "Check on the server",
    serverIssues: "Draft rules the server has not taken into account:",
    serverAgrees: "The server agrees ({{reason}}).",
    serverDiffers:
      "The server says otherwise: {{decision}} ({{reason}}). Other policies or the person may have changed in ways not shown here yet.",
    recent: "Recent URLs on this domain",
    noRecent: "No browsing recorded on this domain.",
    defaultDeny: "No rule allows it: denied by default",
    ruleOfThis: "Rule {{number}} of this policy",
    ruleOfOther: "Rule {{number}} of “{{policy}}”",
    decision: {
      allow: "Allowed",
      deny: "Denied",
    },
    notes: {
      invalidHost: "The URL's domain is not valid.",
      appsInformational:
        "Some rules have apps on login or browsing: for now they are informational only.",
      invalidPath: "A path expression is not valid: it fails closed.",
      originOutside:
        "The website asking for the signature (origin) is not in the domains of a rule that allows the site.",
      loginByDomain:
        "Login is decided by domain; deny rules with paths apply afterwards, on each page.",
      notApplicable: {
        subject: "This policy does not apply to this person.",
        credential: "This policy does not include this certificate.",
        validity: "This policy is not in force now.",
        format: "This policy does not allow this signature format.",
      },
    },
  },
  evaluateReasons: {
    "deny-rule": "a rule denies it",
    "allow-rule": "a rule allows it",
    "default-deny": "no rule allows it",
    "invalid-host": "the domain is not valid",
  },
};
